import React from 'react';
import { pct, sign } from '../../engine/data';
import type { Worker } from '../../engine/data';
import {
  cdiLabel,
  cdiTone,
  lrmLabel,
  lrmTone,
  pliLabel,
  pliTone,
  psiLabel,
  psiTone,
  roleTypeLabel,
} from '../../engine/scores';
import { ScoreLegend } from '../../ScoreWidgets';
import { Kpi, KpiRow, fedTone, toneText } from '../../ui';
import type { Tone } from '../../ui';

/** Higher-is-worse banding: >= bad -> bad, >= warn -> warn, else good. */
const hi = (v: number, warn: number, bad: number): Tone => (v >= bad ? 'bad' : v >= warn ? 'warn' : 'good');
/** Lower-is-worse banding (deltas that go negative). */
const lo = (v: number, warn: number, bad: number): Tone => (v <= bad ? 'bad' : v <= warn ? 'warn' : 'good');
/** PLI/PSI tile: untinted below 35. */
const load = (v: number): Tone | undefined => (v >= 75 ? 'bad' : v >= 55 ? 'warn' : v >= 35 ? 'good' : undefined);

const Unit: React.FC<{ children: string }> = ({ children }) => (
  <small className='text-sm font-normal text-muted-foreground'>{children}</small>
);

export const WorkerKpis: React.FC<{ w: Worker }> = ({ w }) => {
  const dHRV = pct(w.hrv, w.hrv_b);
  const dRHR = pct(w.rhr, w.rhr_b);
  const dSLP = pct(w.slp, w.slp_b);

  return (
    <>
      <ScoreLegend />
      <KpiRow>
        <Kpi
          label='FED — Fatigue Exposure'
          tone={w.fed >= 80 ? 'bad' : w.fed >= 60 ? 'warn' : 'good'}
          value={<span className={toneText[fedTone(w.fed)]}>{w.fed}</span>}
          sub='HRV 45% · RHR 30% · Sleep 25%'
        />
        <Kpi
          label='CDI — Circadian Disruption'
          tone={hi(w.cdi, 35, 60)}
          value={<span className={toneText[cdiTone(w.cdi)]}>{w.cdi}</span>}
          sub={`${cdiLabel(w.cdi)} · Drift ${w.slp_onset_drift || 0}h · HRV offset ${w.hrv_peak_offset || 0}h`}
        />
        <Kpi
          label='LRM — Load Readiness'
          tone={w.lrm > 40 ? 'bad' : w.lrm > 0 ? 'warn' : 'good'}
          value={<span className={toneText[lrmTone(w.lrm)]}>{w.lrm > 0 ? `+${w.lrm}` : w.lrm}</span>}
          sub={`${lrmLabel(w.lrm)} · Ready ${w.lrmReady} · Demand ${w.lrmDemand}`}
        />
        <Kpi
          label='HRV'
          tone={lo(dHRV, -8, -15)}
          value={
            <>
              {w.hrv}
              <Unit>ms</Unit>
            </>
          }
          subTrend={dHRV < 0 ? 'up' : 'dn'}
          sub={`${sign(dHRV)}${dHRV}% vs ${w.hrv_b}ms base`}
        />
        <Kpi
          label='RHR'
          tone={hi(dRHR, 4, 8)}
          value={
            <>
              {w.rhr}
              <Unit>bpm</Unit>
            </>
          }
          subTrend={dRHR > 0 ? 'up' : 'dn'}
          sub={`${sign(dRHR)}${dRHR}% vs ${w.rhr_b}bpm`}
        />
        <Kpi
          label='Last Sleep'
          tone={lo(dSLP, -8, -15)}
          value={
            <>
              {w.slp}
              <Unit>h</Unit>
            </>
          }
          subTrend={dSLP < 0 ? 'up' : 'dn'}
          sub={`${sign(dSLP)}${dSLP}% vs ${w.slp_b}h base`}
        />
        <Kpi
          label='PLI — Physical Load'
          tone={load(w.pli)}
          value={<span className={toneText[pliTone(w.pli)]}>{w.pli}</span>}
          sub={`${pliLabel(w.pli)} · ~${w.pliKcal?.toLocaleString() || '—'} kcal`}
        />
        <Kpi
          label='PSI — Psych Stress'
          tone={load(w.psi)}
          value={<span className={toneText[psiTone(w.psi)]}>{w.psi}</span>}
          sub={`${psiLabel(w.psi)} · ${roleTypeLabel(w.roleType)}`}
        />
      </KpiRow>
    </>
  );
};
